import { useEffect, useState } from "react";
import { Plus, Loader2, X, Trash2, ShieldCheck, KeyRound } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

const inputCls = "border border-brand-line rounded-xl px-4 py-2.5 w-full focus:ring-2 focus:ring-brand-accent focus:border-brand-accent outline-none bg-white text-brand-ink";
const ROLE_LABELS = { patron: "Patron", vendeur: "Vendeur", associe: "Associé" };
const ROLE_DESC = {
  patron: "Accès complet (stock, ventes, bénéfices, utilisateurs)",
  vendeur: "Ventes, livraisons, paiements — sans les marges",
  associe: "Lecture des ventes / notifications",
};

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState(null);
  const [creating, setCreating] = useState(false);
  const [resetting, setResetting] = useState(null);

  const load = () => api.get("/admin/users").then(({ data }) => setUsers(data));
  useEffect(() => { load(); }, []);

  const remove = async (u) => {
    if (!window.confirm(`Supprimer ${u.name} ?`)) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success("Utilisateur supprimé"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (users === null) return <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-brand" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight text-brand-ink">Utilisateurs</h1>
          <p className="text-brand-muted mt-1">Gérez l'équipe : patron, vendeur, associé.</p>
        </div>
        <button onClick={() => setCreating(true)} data-testid="add-user-button" className="bg-brand text-white hover:bg-brand-hover rounded-full px-4 py-2.5 font-semibold flex items-center gap-2 text-sm">
          <Plus className="w-4 h-4" /> Ajouter un membre
        </button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {users.map((u) => (
          <div key={u.id} data-testid={`user-card-${u.id}`} className="bg-white border border-brand-line rounded-2xl p-5">
            <div className="flex items-start justify-between">
              <span className="w-11 h-11 rounded-full bg-brand-accent flex items-center justify-center text-white font-semibold text-lg">{u.name.charAt(0).toUpperCase()}</span>
              <div className="flex items-center gap-1">
                <button onClick={() => setResetting(u)} data-testid={`reset-password-${u.id}`} title="Modifier le membre (nom / mot de passe)" className="p-2 text-brand-sub hover:bg-brand-cream rounded-lg"><KeyRound className="w-4 h-4" /></button>
                {u.role !== "patron" && (
                  <button onClick={() => remove(u)} data-testid={`delete-user-${u.id}`} className="p-2 text-brand-secondary hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                )}
              </div>
            </div>
            <p className="font-heading font-semibold text-brand-ink mt-3">{u.name}{u.id === me.id && <span className="text-xs text-brand-muted"> (vous)</span>}</p>
            <p className="text-sm text-brand-muted">{u.email}</p>
            <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-brand-cream text-brand-ink font-semibold mt-3">
              <ShieldCheck className="w-3.5 h-3.5" /> {ROLE_LABELS[u.role]}
            </span>
          </div>
        ))}
      </div>

      {creating && <NewUserModal onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
      {resetting && <ResetPasswordModal target={resetting} onClose={() => setResetting(null)} onSaved={() => { setResetting(null); load(); }} />}
    </div>
  );
}

function NewUserModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", role: "vendeur" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    setError(""); setLoading(true);
    try {
      await api.post("/admin/users", form);
      toast.success("Membre ajouté");
      onSaved();
    } catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-bold text-xl text-brand-ink">Ajouter un membre</h3>
          <button onClick={onClose} className="p-1.5 hover:bg-brand-cream rounded-full"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <input data-testid="user-name" className={inputCls} placeholder="Nom complet" value={form.name} onChange={set("name")} />
          <input data-testid="user-email" className={inputCls} placeholder="Email" value={form.email} onChange={set("email")} />
          <input data-testid="user-password" type="password" className={inputCls} placeholder="Mot de passe (min. 6)" value={form.password} onChange={set("password")} />
          <input className={inputCls} placeholder="Téléphone (optionnel)" value={form.phone} onChange={set("phone")} />
          <div>
            <select data-testid="user-role" className={inputCls} value={form.role} onChange={set("role")}>
              <option value="vendeur">Vendeur</option>
              <option value="associe">Associé</option>
              <option value="patron">Patron</option>
            </select>
            <p className="text-xs text-brand-muted mt-1">{ROLE_DESC[form.role]}</p>
          </div>
          {error && <p className="text-sm text-brand-secondary">{error}</p>}
          <button onClick={save} disabled={loading} data-testid="save-user-button" className="w-full bg-brand text-white hover:bg-brand-hover rounded-full px-6 py-3 font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Créer le compte"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ResetPasswordModal({ target, onClose, onSaved }) {
  const [name, setName] = useState(target.name || "");
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const save = async () => {
    setError("");
    if (!name.trim()) return setError("Le nom est obligatoire.");
    if (pw && pw.length < 6) return setError("Le mot de passe doit contenir au moins 6 caractères.");
    setLoading(true);
    try {
      await api.patch(`/admin/users/${target.id}`, { name: name.trim(), new_password: pw || undefined });
      toast.success(`Membre ${name.trim()} mis à jour`);
      onSaved ? onSaved() : onClose();
    } catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-bold text-xl text-brand-ink">Modifier le membre</h3>
          <button onClick={onClose} className="p-1.5 hover:bg-brand-cream rounded-full"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-sm text-brand-sub">Nom affiché</label>
            <input data-testid="edit-name-input" type="text" className={inputCls + " mt-1"} placeholder="Ex: Awa Koffi" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div>
            <label className="text-sm text-brand-sub">Nouveau mot de passe (optionnel)</label>
            <input data-testid="reset-password-input" type="text" className={inputCls + " mt-1"} placeholder="Laisser vide pour ne pas changer" value={pw} onChange={(e) => setPw(e.target.value)} />
            <p className="text-xs text-brand-muted mt-1">Le membre pourra le changer après connexion.</p>
          </div>
          {error && <p className="text-sm text-brand-secondary">{error}</p>}
          <button onClick={save} disabled={loading} data-testid="save-reset-password-button" className="w-full bg-brand text-white hover:bg-brand-hover rounded-full px-6 py-3 font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Enregistrer"}
          </button>
        </div>
      </div>
    </div>
  );
}
